// src/dokumentklassen/rechnung/EmailExportDialog.jsx
// Eigenständiges modales Fenster für "Export als Mail" - bewusst nicht über useDialog()/
// Dialog.jsx (das deckt nur Bestätigen/Alert ab), sondern eigene Komponente mit Eingabefeld,
// Lade-Zustand und Fehleranzeige. Visuell an dieselben CSS-Klassen wie Dialog.jsx angelehnt
// (.dialog-overlay/.dialog-box/...), damit es sich nahtlos einfügt.
//
// Die zuletzt benutzte Adresse wird in localStorage gemerkt - bewusst erst NACH erfolgreichem
// Versand (nicht bei jeder Änderung im Feld), damit nie eine kaputte/nicht mehr aktuelle Adresse
// dauerhaft vorausgefüllt wird (Absprache siehe Chat-Verlauf).
import React, { useState } from 'react'

const EMAIL_STORAGE_KEY = 'datenleser.exportEmailAdresse'
// Bewusst nur eine grobe Plausibilitätsprüfung (kein Versuch, RFC 5322 vollständig
// nachzubilden) - die verbindliche Prüfung passiert ohnehin serverseitig
// (EmailAdressValidator). Hier geht es nur darum, den "Senden"-Button erst freizugeben,
// wenn was Sinnvolles drinsteht.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function liesGespeicherteAdresse () {
  try {
    return window.localStorage.getItem(EMAIL_STORAGE_KEY) ?? ''
  } catch {
    return ''
  }
}

function speichereAdresse (adresse) {
  try {
    window.localStorage.setItem(EMAIL_STORAGE_KEY, adresse)
  } catch {
    // localStorage evtl. nicht verfügbar (z.B. Privatmodus) - Adresse wird dann einfach
    // nicht gemerkt, der Versand selbst ist davon nicht betroffen.
  }
}

/**
 * @param onSenden async (adresse) => void - wirft bei Fehlschlag, damit die Fehlermeldung
 *   hier im Dialog angezeigt werden kann statt den Dialog kommentarlos zu schließen.
 * @param onErfolg () => void - wird nach erfolgreichem Versand UND Speichern der Adresse
 *   aufgerufen (schließt den Dialog, zeigt ggf. eine Erfolgsmeldung).
 * @param onAbbrechen () => void - Dialog ohne Versand schließen.
 */
function EmailExportDialog ({ onSenden, onErfolg, onAbbrechen }) {
  const [adresse, setAdresse] = useState(liesGespeicherteAdresse)
  const [sendeLaeuft, setSendeLaeuft] = useState(false)
  const [fehler, setFehler] = useState(null)

  const adresseGetrimmt = adresse.trim()
  const gueltig = EMAIL_REGEX.test(adresseGetrimmt)

  const handleSenden = async () => {
    if (!gueltig || sendeLaeuft) return
    setSendeLaeuft(true)
    setFehler(null)
    try {
      await onSenden(adresseGetrimmt)
      speichereAdresse(adresseGetrimmt)
      onErfolg()
    } catch (err) {
      setFehler(
        err.response?.data?.detail ||
          err.message ||
          'E-Mail konnte nicht versendet werden.'
      )
      setSendeLaeuft(false)
    }
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleSenden()
  }

  return (
    <div
      className='dialog-overlay'
      onClick={sendeLaeuft ? undefined : onAbbrechen}
    >
      <div
        className='dialog-box'
        onClick={e => e.stopPropagation()}
        role='dialog'
        aria-modal='true'
      >
        <h3 className='dialog-titel'>Export als Mail</h3>
        <div className='dialog-nachricht'>
          <p>Die Excel-Datei wird als Anhang an folgende Adresse versendet:</p>
          <input
            type='email'
            className='email-export-input'
            value={adresse}
            onChange={e => setAdresse(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='empfaenger@beispiel.de'
            disabled={sendeLaeuft}
            autoFocus
          />
          {fehler && <div className='error-banner'>{fehler}</div>}
        </div>
        <div className='dialog-aktionen'>
          <button
            type='button'
            className='btn'
            onClick={onAbbrechen}
            disabled={sendeLaeuft}
          >
            Abbrechen
          </button>
          <button
            type='button'
            className='btn btn-primary'
            onClick={handleSenden}
            disabled={!gueltig || sendeLaeuft}
          >
            {sendeLaeuft ? 'Sende…' : 'Senden'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EmailExportDialog
